import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Home, AlertTriangle, Phone, Shield, Activity, Brain, Heart, Zap, Clock, ArrowRight } from "lucide-react";
import { Header, Footer } from "../../../components/Layout";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";

const PastaBasePage = () => {
  const signs = [
    { title: "Deterioro físico acelerado", desc: "Pérdida de peso extrema, ojeras, palidez, descuido personal en cuestión de semanas." },
    { title: "Consumo compulsivo", desc: "Necesidad de fumar cada pocos minutos. El efecto dura 5-10 min y deja un craving intenso." },
    { title: "Conductas riesgosas", desc: "Robos, peleas, comercio sexual para conseguir la siguiente dosis (\"jalar\" o \"quemar\")." },
    { title: "Aislamiento y paranoia", desc: "Desconfianza extrema, alucinaciones, pérdida de contacto con familia y trabajo." },
    { title: "Sulfato / hedor característico", desc: "La pasta base se fuma en pipas caseras con olor a plástico quemado muy característico." },
    { title: "Problemas respiratorios severos", desc: "Tos persistente, esputo negro, infecciones pulmonares recurrentes." }
  ];

  const phases = [
    { phase: "Desintoxicación", days: "7-14 días", desc: "Internación médica. El craving de pasta base es brutal y requiere contención 24/7.", icon: Shield },
    { phase: "Residencial intensivo", days: "3-6 meses", desc: "Comunidad terapéutica o residencial. Ambulatorio raramente funciona en pasta base.", icon: Activity },
    { phase: "Reinserción laboral y familiar", days: "6-12 meses", desc: "Acompañamiento con talleres, apoyo familiar y seguimiento ambulatorio.", icon: Heart },
    { phase: "Prevención de recaídas", days: "indefinido", desc: "Reuniones de pares (NA, CoDA), terapia individual y activación de redes de apoyo.", icon: Brain }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      
        <title>Adicción a Pasta Base: Síntomas, Tratamiento y Rehabilitación en Chile | SinAdicciones.org</title>
        <meta name="description" content="Todo sobre la adicción a pasta base (PBC) en Chile: señales de alarma, fases del tratamiento y centros de rehabilitación residencial especializados. Guía completa 2026." />
        <link rel="canonical" href="https://sinadicciones.org/adicciones/pasta-base" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/adicciones/pasta-base" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <meta property="og:title" content="Adicción a Pasta Base en Chile: Tratamiento y Centros de Rehabilitación" />
        <meta property="og:description" content="Guía completa: síntomas, riesgos y dónde tratarse. Directorio nacional de centros especializados en pasta base." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalCondition",
          "name": "Adicción a Pasta Base (PBC)",
          "alternateName": ["Dependencia a pasta base", "Adicción a PBC", "Crack de cocaína"],
          "description": "Trastorno por consumo de pasta base de cocaína, caracterizado por alta dependencia física y psicológica en corto tiempo.",
          "associatedAnatomy": { "@type": "AnatomicalStructure", "name": "Sistema nervioso central" },
          "possibleTreatment": [
            { "@type": "MedicalTherapy", "name": "Internación residencial" },
            { "@type": "MedicalTherapy", "name": "Desintoxicación supervisada" },
            { "@type": "MedicalTherapy", "name": "Terapia cognitivo-conductual" },
            { "@type": "MedicalTherapy", "name": "Comunidad terapéutica" }
          ]
        })}</script>
      
      <Header />
      <main className="pt-20 pb-16" data-testid="pasta-base-page">
        {/* Breadcrumbs */}
        <div className="bg-slate-900 border-b border-slate-800">
          <div className="max-w-5xl mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-slate-400">
              <Link to="/" className="hover:text-emerald-400 flex items-center gap-1"><Home className="w-3 h-3" />Inicio</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/adicciones" className="hover:text-emerald-400">Adicciones</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-slate-300">Pasta Base</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-b from-red-950/40 to-slate-950 border-b border-slate-800">
          <div className="max-w-5xl mx-auto px-4 py-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/10 border border-red-500/30 rounded-full text-red-400 text-xs font-medium mb-4">
              <AlertTriangle className="w-3 h-3" /><span>Sustancia de extremo riesgo</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="pastabase-title">
              Adicción a Pasta Base: Guía de Rehabilitación en Chile
            </h1>
            <p className="text-lg text-slate-400 max-w-3xl leading-relaxed">
              La pasta base (PBC) es una de las drogas más devastadoras en Chile. Su efecto es breve pero extremadamente adictivo.
              El tratamiento requiere internación y acompañamiento intensivo. Aquí te explicamos qué buscar y dónde acudir.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                <div className="text-2xl font-bold text-red-400">5-10 min</div>
                <div className="text-xs text-slate-500 mt-1">Duración del efecto</div>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                <div className="text-2xl font-bold text-red-400">Inmediata</div>
                <div className="text-xs text-slate-500 mt-1">Velocidad de adicción</div>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                <div className="text-2xl font-bold text-emerald-400">3-6 meses</div>
                <div className="text-xs text-slate-500 mt-1">Internación típica</div>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                <div className="text-2xl font-bold text-emerald-400">75%+</div>
                <div className="text-xs text-slate-500 mt-1">Recuperación con tratamiento</div>
              </div>
            </div>
          </div>
        </section>

        {/* Signs */}
        <section className="max-w-5xl mx-auto px-4 py-12">
          <h2 className="text-3xl font-bold mb-2">Señales de alarma</h2>
          <p className="text-slate-400 mb-8">La pasta base deja huellas claras. Si reconoces varias de estas señales en un familiar, actúa ahora.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {signs.map(function(s, i) {
              return (
                <Card key={i} className="bg-slate-900/50 border-slate-800">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-red-500/10 text-red-400 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-4 h-4" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-50 mb-1">{s.title}</h3>
                        <p className="text-sm text-slate-400">{s.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Treatment phases */}
        <section className="max-w-5xl mx-auto px-4 py-8">
          <h2 className="text-3xl font-bold mb-2">Fases del tratamiento</h2>
          <p className="text-slate-400 mb-8">Un tratamiento efectivo para pasta base suele contemplar estas etapas:</p>
          <div className="space-y-4">
            {phases.map(function(p, i) {
              var Icon = p.icon;
              return (
                <Card key={i} className="bg-slate-900/50 border-slate-800">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                          <h3 className="font-semibold text-slate-50">Fase {i + 1}: {p.phase}</h3>
                          <span className="text-xs px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded-full flex items-center gap-1"><Clock className="w-3 h-3" />{p.days}</span>
                        </div>
                        <p className="text-sm text-slate-400">{p.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Where to go */}
        <section className="max-w-5xl mx-auto px-4 py-12">
          <div className="bg-gradient-to-br from-emerald-900/30 to-slate-900 border border-emerald-500/20 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-3">¿Dónde tratarse en Chile?</h2>
            <p className="text-slate-400 mb-6">Busca centros residenciales o comunidades terapéuticas con experiencia en pasta base. Evita tratamientos ambulatorios: el craving es demasiado intenso en etapas iniciales.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                <Link to="/tratamiento/residencial" data-testid="pastabase-cta-residencial">Ver centros residenciales <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/10">
                <a href="tel:1412" data-testid="pastabase-fonodrogas"><Phone className="w-4 h-4 mr-2" />Llamar Fonodrogas 1412</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Related */}
        <section className="max-w-5xl mx-auto px-4 py-8">
          <h3 className="text-lg font-semibold mb-4">Relacionados</h3>
          <div className="flex flex-wrap gap-2">
            <Link to="/adicciones/cocaina" className="px-4 py-2 bg-slate-900 border border-slate-800 hover:border-emerald-500/50 rounded-full text-sm text-slate-300 hover:text-emerald-400 transition">Adicción a cocaína</Link>
            <Link to="/adicciones/patologia-dual" className="px-4 py-2 bg-slate-900 border border-slate-800 hover:border-emerald-500/50 rounded-full text-sm text-slate-300 hover:text-emerald-400 transition">Patología dual</Link>
            <Link to="/tratamiento/residencial" className="px-4 py-2 bg-slate-900 border border-slate-800 hover:border-emerald-500/50 rounded-full text-sm text-slate-300 hover:text-emerald-400 transition">Tratamiento residencial</Link>
            <Link to="/region/metropolitana" className="px-4 py-2 bg-slate-900 border border-slate-800 hover:border-emerald-500/50 rounded-full text-sm text-slate-300 hover:text-emerald-400 transition">Centros en Santiago</Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PastaBasePage;
